import type { DtcCodeEntity } from '@packages/domain/dtc-codes';
import type { VinDetailEntity } from '@packages/domain/vin';

import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import {
    ClearFailCode,
    CloseCase,
    DEFAULT_ALLOW_CASE_FEATURES,
    DeleteCase,
    type FailCodeData,
    FetchCaseDetails,
    selectPrimaryRetailer,
    SubmitCase,
    type TechlineCaseEntity,
    TechlineStatusCode,
    UpdateCase,
    UpdateFailCode,
} from '@packages/domain/techline/cases';
import { DeleteCidCvnCase, SaveCase, SubmitCidCvnCase } from '@packages/domain/techline/cid-cvn';
import { AddTeamMember } from '@packages/domain/techline/members';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics/google-analytics.events';
import { container } from '@web/services/container';
import { googleAnalyticsService } from '@web/services/singletons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WorksheetOpenMode } from '../worksheet-group-accordion/worksheet-group-accordion.types';

import { broadcastCaseStatusChange } from '../../utils/case-tab-sync';
import {
    locateWorksheetBySubmissionId,
    locateWorksheetByWorksheetId,
} from '../worksheet-group-accordion/worksheet-group.helpers';
import { canEditDraftForRoles } from './can-edit-draft';
import { useAssets } from './use-assets';
import { useAssignments } from './use-assignments';
import { useComments } from './use-comments';
import { useDraftStepperValidation } from './use-draft-stepper-validation';
import { useDtcCodes } from './use-dtc-codes';
import { useTechlineCaseAnalytics } from './use-techline-case-analytics';
import { useWorksheetSubmissions } from './use-worksheet-submissions';

const logger = getLogger('techline');

// Operations are prefixed with $ to make them visually distinct in this file.
const [
    $addTeamMember,
    $clearFailCode,
    $closeCase,
    $deleteCase,
    $deleteCidCvnCase,
    $fetchCaseDetails,
    $updateCase,
    $saveCidCvnCase,
    $submitCase,
    $submitCidCvnCase,
    $updateFailCode,
] = container.resolveOperations(
    AddTeamMember,
    ClearFailCode,
    CloseCase,
    DeleteCase,
    DeleteCidCvnCase,
    FetchCaseDetails,
    UpdateCase,
    SaveCase,
    SubmitCase,
    SubmitCidCvnCase,
    UpdateFailCode
);

/** Describes which worksheet is currently open in the inline editor. */
export type EditingWorksheet = {
    /** The current case status, used to determine worksheet behavior. */
    caseStatus: TechlineStatusCode;
    /** The worksheet submission ID (undefined for new submissions). */
    submissionId?: string;
    /** The worksheet definition ID. */
    worksheetId: string;
    /** The mode in which the worksheet is opened (view, edit, or add). */
    mode: WorksheetOpenMode;
};

/**
 * Composition hook that owns all state and orchestrates all API interactions
 * for a techline case. Child components receive data and callbacks via props;
 * they never call services directly.
 *
 * All business logic is delegated to use cases resolved from the IoC container.
 * This hook owns only React state, derived state, and thin useCallback wrappers
 * that call use cases and update state.
 *
 * @param caseNumber - the case number to manage (undefined while loading)
 * @returns the full case state and mutation interface
 */
export function useTechlineCase(caseNumber: string | undefined) {
    const { account } = useAuthState();
    const [vehicleVinData, setVehicleVinData] = useState<VinDetailEntity>();
    const [techlineCaseData, setTechlineCaseData] = useState<TechlineCaseEntity>();
    const [dtcCodeData, setDtcCodeData] = useState<DtcCodeEntity[]>([]);
    const [worksheetGroupData, setWorksheetGroupData] = useState<
        TechlineCaseEntity['WorksheetSubmissionGroups'] | null
    >(null);
    const [editingWorksheet, setEditingWorksheet] = useState<EditingWorksheet | null>(null);

    // -- Asset error state --
    const [assetErrorMessage, setAssetErrorMessage] = useState('');
    const assetErrorTimerRef = useRef<ReturnType<typeof setTimeout>>();

    /**
     * Handle an asset upload or processing error by setting the error message
     * and auto-dismissing after 10 seconds.
     *
     * @param filename - the name of the file that failed
     * @param error - the error description
     */
    const handleAssetError = useCallback((filename: string, error: string) => {
        setAssetErrorMessage(`${error}: ${filename}`);
        clearTimeout(assetErrorTimerRef.current);
        assetErrorTimerRef.current = setTimeout(() => setAssetErrorMessage(''), 10000);
    }, []);

    /** Dismiss the asset error alert manually. */
    const handleAssetErrorDismiss = useCallback(() => {
        setAssetErrorMessage('');
        clearTimeout(assetErrorTimerRef.current);
    }, []);

    // -- Fail code state (replaces PendingTechlineProvider) --
    const [showFailCodeModal, setShowFailCodeModal] = useState(false);
    const [failCodeData, setFailCodeData] = useState<FailCodeData | null>(null);
    const [isFailCodeSaving, setIsFailCodeSaving] = useState(false);
    const [failCodeErrors, setFailCodeErrors] = useState<string[]>([]);

    // -- Derived state --

    /**
     * Select the primary retailer profile from case details, with fallback
     * chain through unique profiles, all profiles, and first entries.
     */
    const primaryRetailerData = useMemo(
        () => selectPrimaryRetailer(techlineCaseData),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [techlineCaseData?.retailerUniqueProfiles, techlineCaseData?.retailerProfiles]
    );

    const retailerNumber = primaryRetailerData?.retailerNumber;

    /**
     * Flags indicating which features are allowed for the current case
     * Default is to show all features.
     */

    const allowCaseFeatures = techlineCaseData?.allowCaseFeatures ?? DEFAULT_ALLOW_CASE_FEATURES;

    /** Whether the current user can edit draft cases (corporate-only users cannot). */
    const canEditDraft = useMemo(() => canEditDraftForRoles(account?.baseRoleIds), [account]);

    /**
     * Whether the current user can edit submitted cases. Users with the DSQM role
     * cannot edit submitted cases.
     */
    const canEditSubmittedCase = useMemo(() => {
        const isDSQM = Array.isArray(account?.baseRoleIds) && account.baseRoleIds.includes('DSQM');
        return !isDSQM;
    }, [account]);

    /**
     * Update worksheetGroupData state and sync the groups into techlineCaseData
     * when the worksheet submissions subordinate hook signals a change (e.g.
     * after refresh or delete). Syncing into techlineCaseData ensures that
     * downstream consumers reading caseDetails.WorksheetSubmissionGroups
     * (e.g. the stepper's checkbox re-merge effect) see the fresh data.
     *
     * @param groups - the updated worksheet submission groups
     */
    const handleWorksheetGroupDataChange = useCallback(
        (groups: TechlineCaseEntity['WorksheetSubmissionGroups'] | null) => {
            setWorksheetGroupData(groups);
            setTechlineCaseData((prev) => (prev ? { ...prev, WorksheetSubmissionGroups: groups ?? undefined } : prev));
        },
        []
    );

    /**
     * Update dtcCodeData state when DTC codes are added, removed, or modified
     * by the dtcCodes subordinate hook.
     */
    const handleDtcCodeDataChange = useCallback(
        (dtcCodes: DtcCodeEntity[] | ((prev: DtcCodeEntity[]) => DtcCodeEntity[])) => {
            setDtcCodeData(dtcCodes);
        },
        []
    );

    // -- Data fetchers (defined before subordinate hooks so they can be passed as callbacks) --

    /**
     * Fetch full case details from NorthStar, update techlineCaseData,
     * vehicleVinData, worksheetGroupData, and failCodeData state.
     */
    const fetchCaseDetails = useCallback(async (caseNumber: string) => {
        try {
            const result = await $fetchCaseDetails({ caseNumber });
            if (result?.caseDetails) {
                setTechlineCaseData(result.caseDetails);
                setWorksheetGroupData(result.caseDetails.WorksheetSubmissionGroups ?? null);
                setDtcCodeData(result.caseDetails.diagnosticTroubleCodes ?? []);
                setFailCodeData({
                    repairCode: result.caseDetails.repairCode ?? '',
                    failCode: result.caseDetails.failCode ?? '',
                    section: result.caseDetails.section ?? '',
                    subSection: result.caseDetails.subSection ?? '',
                });
            }
            if (result?.vinDetails) {
                setVehicleVinData(result.vinDetails);
            }
        } catch (err) {
            alert('Error: ' + err + '. Please refresh & try again.');
            logger.error(`Failed to fetch case details [${caseNumber}]`, { caseNumber, error: err });
        }
    }, []);

    // -- Subordinate hooks --

    // Comments and assignments are only relevant for non-draft cases
    const isDraftCase = !techlineCaseData || techlineCaseData.techlineStatusCode === TechlineStatusCode.DRAFT;

    const assets = useAssets(caseNumber, techlineCaseData?.techlineStatusCode, isDraftCase, handleAssetError);

    const assignments = useAssignments(caseNumber, !isDraftCase);

    const comments = useComments({
        caseNumber,
        caseStatusCode: techlineCaseData?.techlineStatusCode,
        isEnabled: !isDraftCase,
        onCommentPosted: useCallback(() => {
            if (caseNumber) {
                fetchCaseDetails(caseNumber);
            }
        }, [caseNumber, fetchCaseDetails]),
        retailerNumber,
        username: account?.soaUsername,
    });

    const dtcCodes = useDtcCodes({
        caseNumber: caseNumber || '',
        dtcCodeData,
        onDtcCodeChange: handleDtcCodeDataChange,
    });

    const worksheetSubmissions = useWorksheetSubmissions({
        caseNumber,
        onWorksheetGroupDataChange: handleWorksheetGroupDataChange,
        worksheetGroupData,
    });

    const draftStepperValidation = useDraftStepperValidation(
        techlineCaseData,
        worksheetSubmissions.enrichedWorksheetGroupData,
        worksheetSubmissions.worksheetValidationResults
    );

    // -- Analytics --
    const editingWorksheetGroup = useMemo(() => {
        if (!editingWorksheet) {
            return undefined;
        }
        const locatedWorksheet = editingWorksheet.submissionId
            ? locateWorksheetBySubmissionId(
                  worksheetSubmissions.enrichedWorksheetGroupData,
                  editingWorksheet.submissionId
              )
            : locateWorksheetByWorksheetId(
                  worksheetSubmissions.enrichedWorksheetGroupData,
                  editingWorksheet.worksheetId
              );
        return locatedWorksheet?.groupName;
    }, [editingWorksheet, worksheetSubmissions.enrichedWorksheetGroupData]);

    /** Side effect hook that registers the Techline Case in the analytics layer. */
    useTechlineCaseAnalytics({
        caseNumber,
        techlineCaseData,
        vehicleVinData,
        editingWorksheet,
        editingWorksheetGroup,
    });

    // -- Derived state (depends on subordinate hooks) --

    /**
     * True while initial data is still being fetched (case details and/or
     * worksheet submissions). Consumers should show a loading indicator
     * while this is true.
     */
    const isInitialDataLoading = !techlineCaseData || !worksheetSubmissions.hasLoadedInitialSubmissions;

    /** Fetch case details on mount. */
    useEffect(() => {
        if (caseNumber) {
            fetchCaseDetails(caseNumber);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseNumber]);

    // -- Handlers --

    /** Shallow-merge partial updates into the existing techlineCaseData. */
    const handleTechlineCaseDataChange = useCallback((data: Partial<TechlineCaseEntity>) => {
        setTechlineCaseData((prev) => {
            if (!prev) {
                return prev;
            }
            return { ...prev, ...data };
        });
    }, []);

    // -- Mutations --

    /** Update the current case details via the API. */
    const saveCase = useCallback(async (): Promise<boolean> => {
        if (!caseNumber || !techlineCaseData) {
            return false;
        }

        try {
            const success = await $updateCase({
                techlineCaseData,
                caseNumber,
                vin: vehicleVinData?.vin,
                username: account?.soaUsername || '',
                retailerNumber,
            });
            googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSaveDraftCase, {
                success,
                case_status: techlineCaseData.techlineStatusCode,
                case_type: techlineCaseData?.primaryCaseType,
            });
            return success;
        } catch (error) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSaveDraftCase, { success: false });
            throw error;
        }
    }, [caseNumber, techlineCaseData, vehicleVinData, account, retailerNumber]);

    /**
     * Update, submit the case for review, then refresh all case data so the
     * view transitions from DraftCaseSubview to SubmittedCaseSubview. Broadcasts
     * the status change to any open dashboard tabs.
     */
    const submitCase = useCallback(async (): Promise<boolean> => {
        if (!caseNumber || !techlineCaseData) {
            return false;
        }

        // Submit the case without re-fetching; the caller navigates to the
        // dashboard immediately after, so fresh case data is not needed.
        try {
            const result = await $submitCase({
                caseNumber,
                retailerNumber,
                shouldSkipRefresh: true,
                techlineCaseData,
                username: account?.soaUsername || '',
                vin: vehicleVinData?.vin,
                worksheetSubmissionMap: worksheetSubmissions.worksheetSubmissionMap,
            });

            if (!result?.caseDetails) {
                googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSubmitCase, { success: false });
                return false;
            }

            // Notify any open dashboard tabs that the case status changed
            broadcastCaseStatusChange({ caseNumber, retailerNumber });

            googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSubmitCase, {
                success: true,
                case_status: TechlineStatusCode.IN_REVIEW,
                case_type: techlineCaseData?.primaryCaseType,
            });
            return true;
        } catch (error) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSubmitCase, { success: false });
            throw error;
        }
    }, [account, caseNumber, retailerNumber, techlineCaseData, vehicleVinData, worksheetSubmissions]);

    /** Delete the current draft case. */
    const deleteCase = useCallback(async (): Promise<boolean> => {
        if (!caseNumber) {
            return false;
        }
        return $deleteCase({ caseNumber });
    }, [caseNumber]);

    /** Close the case with a description and vehicle repair status. */
    const closeCase = useCallback(
        async (params: {
            /** The closing description or resolution note. */
            description: string;

            /** Whether the vehicle was successfully repaired. */
            isVehicleRepaired: boolean;
        }): Promise<boolean> => {
            if (!caseNumber) {
                return false;
            }
            try {
                const success = await $closeCase({
                    caseNumber,
                    username: account?.soaUsername || '',
                    retailerNumber,
                    description: params.description,
                    isVehicleRepaired: params.isVehicleRepaired,
                });

                if (success) {
                    broadcastCaseStatusChange({ caseNumber, retailerNumber });
                }
                googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineCloseCase, {
                    success,
                    case_status: TechlineStatusCode.CLOSE_RESOLVED,
                    case_type: techlineCaseData?.primaryCaseType,
                });
                return success;
            } catch (error) {
                googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineCloseCase, { success: false });
                throw error;
            }
        },
        [caseNumber, account, retailerNumber, techlineCaseData?.primaryCaseType]
    );

    // -- Fail code mutations --

    /** Open the fail code modal. */
    const openFailCodeModal = useCallback(() => {
        setShowFailCodeModal(true);
    }, []);

    /** Close the fail code modal. */
    const closeFailCodeModal = useCallback(() => {
        setShowFailCodeModal(false);
    }, []);

    /**
     * Update fail code fields with optimistic UI. Sets failCodeData immediately,
     * calls the API, and rolls back on error.
     */
    const updateFailCode = useCallback(
        async (data: FailCodeData) => {
            const previousFailCodeData = failCodeData;
            setFailCodeErrors([]);
            setFailCodeData(data);
            setIsFailCodeSaving(true);
            setShowFailCodeModal(false);

            if (!caseNumber) {
                return;
            }

            try {
                await $updateFailCode({
                    caseNumber,
                    vin: vehicleVinData?.vin,
                    username: account?.soaUsername || '',
                    retailerNumber,
                    failCodeData: data,
                });
            } catch {
                setFailCodeData(previousFailCodeData);
                setFailCodeErrors(['Failed to update case. Please refresh and try again.']);
            } finally {
                setIsFailCodeSaving(false);
            }
        },
        [caseNumber, vehicleVinData, account, retailerNumber, failCodeData]
    );

    /**
     * Clear the fail code by setting all fields to empty strings via the
     * ClearFailCode use case, then reset local state.
     */
    const clearFailCode = useCallback(async () => {
        const previousFailCodeData = failCodeData;
        setFailCodeErrors([]);
        setFailCodeData({ repairCode: '', failCode: '', section: '', subSection: '' });
        setIsFailCodeSaving(true);
        setShowFailCodeModal(false);

        if (!caseNumber) {
            return;
        }

        try {
            await $clearFailCode({
                caseNumber,
                vin: vehicleVinData?.vin,
                username: account?.soaUsername || '',
                retailerNumber,
            });
        } catch {
            setFailCodeData(previousFailCodeData);
            setFailCodeErrors(['Failed to clear fail code. Please refresh and try again.']);
        } finally {
            setIsFailCodeSaving(false);
        }
    }, [caseNumber, vehicleVinData, account, retailerNumber, failCodeData]);

    // -- Team mutations --

    /**
     * Add a team member to the case, then re-fetch case details to pick up
     * the new retailer profile.
     */
    const addTeamMember = useCallback(
        async (username: string): Promise<boolean> => {
            if (!caseNumber) {
                return false;
            }
            try {
                const success = await $addTeamMember({
                    caseNumber,
                    username,
                    retailerNumber: retailerNumber || '',
                });
                if (!success) {
                    return false;
                }
                await fetchCaseDetails(caseNumber);
                return true;
            } catch (error) {
                logger.error(`Failed to add team member [${caseNumber}]`, { caseNumber, error });
                return false;
            }
        },
        [caseNumber, retailerNumber, fetchCaseDetails]
    );

    // -- CID-CVN mutations --

    /** Save CID-CVN case details. */
    const saveCidCvnCase = useCallback(
        async (formData: {
            /** The repair order number for the CID-CVN case. */
            repairOrderNumber: string;

            /** The date the repair order was opened. */
            repairOrderOpenDate: string;

            /** The vehicle mileage at the time of the repair order. */
            mileage: string;
        }) => {
            if (!caseNumber || !account) {
                return;
            }
            try {
                await $saveCidCvnCase({
                    caseNumber,
                    vin: vehicleVinData?.vin,
                    username: account.soaUsername,
                    retailerNumber,
                    formData,
                });
                googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSaveDraftCase, {
                    success: true,
                    case_status: techlineCaseData?.techlineStatusCode,
                    case_type: techlineCaseData?.primaryCaseType,
                });
            } catch (error) {
                googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSaveDraftCase, { success: false });
                throw error;
            }
        },
        [caseNumber, vehicleVinData, account, retailerNumber, techlineCaseData]
    );

    /** Submit a CID-CVN case for review. */
    const submitCidCvnCase = useCallback(
        async (submissionId: string, worksheetId: string) => {
            if (!caseNumber) {
                return;
            }
            try {
                await $submitCidCvnCase({
                    caseNumber,
                    username: account?.soaUsername || '',
                    retailerNumber,
                    submissionId,
                    worksheetId,
                });
                googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSubmitCase, {
                    success: true,
                    case_status: TechlineStatusCode.IN_REVIEW,
                    case_type: techlineCaseData?.primaryCaseType,
                });
            } catch (error) {
                googleAnalyticsService.track(GoogleAnalyticsEvents.TechlineSubmitCase, { success: false });
                throw error;
            }
        },
        [caseNumber, account, retailerNumber, techlineCaseData?.primaryCaseType]
    );

    /** Delete a CID-CVN draft case. */
    const deleteCidCvnCase = useCallback(async (): Promise<boolean> => {
        if (!caseNumber) {
            return false;
        }
        return $deleteCidCvnCase({ caseNumber });
    }, [caseNumber]);

    return {
        // Subordinate hooks (spread)
        ...assets,
        ...assignments,
        ...dtcCodes,
        ...comments,
        ...worksheetSubmissions,

        // Draft stepper validation (passed as a unit, not spread)
        draftStepperValidation,

        // State

        /** Error message from a failed asset upload or processing. Empty when no error. */
        assetErrorMessage,

        /** Flags indicating which features are allowed for the current case. */
        allowCaseFeatures,

        /** Diagnostic trouble code data for the case. */
        dtcCodeData,

        /** Which worksheet is open in the inline editor; null means the case view is showing. */
        editingWorksheet,

        /** The current fail code data for the case. Null when no fail code is set. */
        failCodeData,

        /** Errors from fail code operations (update or clear). */
        failCodeErrors,

        /** True while a fail code update or clear is saving to the API. */
        isFailCodeSaving,

        /** Whether the fail code modal is visible. */
        showFailCodeModal,

        /** The full Techline case record from NorthStar. Undefined until the first fetch completes. */
        techlineCaseData,

        /** The current user's SOA username, used for worksheet submission payloads. */
        userName: account?.soaUsername || '',

        /** VIN details for the vehicle associated with the case. */
        vehicleVinData,

        /** Worksheet submission groups for the case, fetched and refreshed independently from case data. */
        worksheetGroupData,

        // Derived

        /** True when the current user can edit draft cases (false for CORPORATE_USER role). */
        canEditDraft,

        /** Whether the current user can edit submitted cases. Users with the DSQM role cannot edit submitted cases. */
        canEditSubmittedCase,

        /** True while initial data (case details + worksheet submissions) is still loading. */
        isInitialDataLoading,

        /** The primary retailer profile, with fallback through unique profiles and all profiles. */
        primaryRetailerData,

        // Setters

        /** Set to open a worksheet in the inline editor, or null to close and return to the case view. */
        setEditingWorksheet,

        // Handlers

        /** Dismiss the asset error alert bar. */
        handleAssetErrorDismiss,

        /** Shallow-merge partial updates into the existing techlineCaseData. Used by stepper form fields. */
        handleTechlineCaseDataChange,

        // Mutations

        /** Close the case (status -> CLOSED) with a description and vehicle repair status. */
        closeCase,

        /** Delete the current draft case. Returns true on success. */
        deleteCase,

        /** Update the current case details to the API. Returns true on success. */
        saveCase,

        /** Update case details, then submit (status -> IN_REVIEW). Returns true on success. */
        submitCase,

        // Fail code mutations

        /** Clear the fail code from the case (sets all fields to empty). */
        clearFailCode,

        /** Close the fail code modal. */
        closeFailCodeModal,

        /** Open the fail code modal. */
        openFailCodeModal,

        /** Update fail code fields with optimistic UI; rolls back on API error. */
        updateFailCode,

        // Team mutations

        /** Add a team member to the case by username, then re-fetch case details. */
        addTeamMember,

        // CID-CVN mutations

        /** Delete a CID-CVN draft case. Returns true on success. */
        deleteCidCvnCase,

        /** Save CID-CVN case details (repair order, date, mileage). */
        saveCidCvnCase,

        /** Submit a CID-CVN case for review (status -> IN_REVIEW). */
        submitCidCvnCase,
    };
}
